import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Modal from "react-modal";
import { config } from "../../config/const";
import "./StoryForm.css";
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';


const ProgressiveImage = ({ src, alt, onRegenerate, index }) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setLoadingProgress(0);
    setImageLoaded(false);

    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setImageLoaded(true);
          return 100;
        }
        return prev + 5;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [src]);

  return (
    <div className="progressive-image-container">
      <img 
        src={src} 
        alt={alt}
        className="story-image"
        style={{
          clipPath: `inset(0 0 ${100 - loadingProgress}% 0)`,
          transition: 'clip-path 0.3s ease-out'
        }}
        onLoad={() => setImageLoaded(true)}
      />
      {imageLoaded && (
        <button 
          className="regenerate-btn"
          onClick={() => onRegenerate(index)}
          title="Regenerate image"
        >
          <svg 
            viewBox="0 0 24 24" 
            width="16" 
            height="16"
          >
            <path 
              fill="currentColor" 
              d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

const StoryForm = ({ selectedImages = [] }) => {
  const defaultStoryIdea = "Create a story about a girl named Elara who travels to Egypt to learn about the mummies. Make the story interesting by adding many twists and turns and providing factual information for kids to learn from...";
  
  const defaultVisualIdea ="Elara is a six year old girl who wears a short blue color frock and is from northern India. She has brown curly short hair. Create only cartoon characters without any text written on the image."


  // Update initial state to use default values
  const [storyIdeas, setStoryIdeas] = useState(defaultStoryIdea);
  const [visualIdeas, setVisualIdeas] = useState(defaultVisualIdea);
  const [story, setStory] = useState("");
  const [paragraphs, setParagraphs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [title, setTitle] = useState("");
  const [activeTab, setActiveTab] = useState("story");
  const [questions, setQuestions] = useState([]);
  const [email, setEmail] = useState(localStorage.getItem("email"));
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [storyWithImages, setStoryWithImages] = useState([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState('');
  const [storyStatus, setStoryStatus] = useState('public');
  const [storyId, setStoryId] = useState(null);
  const [selectedImagesList, setSelectedImagesList] = useState(selectedImages);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [visualMode, setVisualMode] = useState('prompt');
  const [selectedCartoonStyle, setSelectedCartoonStyle] = useState(null);
  const [children, setChildren] = useState([]);
  let [firstGenerationId, setFirstGenerationId] = useState('');
  const firstGenerationIdRef = useRef('');

  const navigate = useNavigate();

  const convertToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const randomSeed = Math.floor(Math.random() * 9999999998) + 1;

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const response = await axios.get(`${config.backend_url}/user/children`, {
          params: { email }
        });
        setChildren(response.data.children.filter(child => child.cartoonImage));
      } catch (error) {
        console.error('Error fetching children:', error);
      }
    };

    fetchChildren();
  }, [email]);

  // Update handleGenerateStory
  const handleGenerateStory = async () => {
    if (!storyIdeas.trim()) return;
    
    setLoading(true);
    setShowForm(false);
    
    try {
      // Convert selected images to base64
      const imagePromises = selectedImagesList.map(async (imageUrl) => {
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          return await convertToBase64(blob);
        } catch (error) {
          console.error('Error converting image:', error);
          return null;
        }
      });

      const base64Images = await Promise.all(imagePromises);
      const validImages = base64Images.filter(img => img !== null);
      
      const baseUrl = config?.backend_url || 'http://localhost:3002';
      const response = await axios.post(`${baseUrl}/user/generateStory`, {
        email,
        prompt: storyIdeas,
        numParagraphs: 6,
        selectedImages: validImages
      });
      if (response.data) {
        const storyContent = response.data.content;
        setParagraphs(storyContent); // Store paragraphs for later use
        setStory(storyContent.join('\n\n'));
        setTitle(response.data.title);
        setTags(response.data.tag?.split('#').filter(tag => tag.trim()) || []);
      }
    } catch (error) {
      console.error("Story Generation Error:", error);
      alert("Failed to generate story. Please try again.");
      setShowForm(true); // Show form again if error occurs
    } finally {
      setLoading(false);
    }
  };

  // Update handleEditSave to preserve paragraph formatting
  const handleEditSave = () => {
    setIsEditing(false);
    // Split by double newline to maintain paragraph separation
    const editedParagraphs = story.split('\n\n').filter(p => p.trim());
    setParagraphs(editedParagraphs);
    // Keep original formatting when joining
    setStory(editedParagraphs.join('\n\n'));
  };

  const handleCreateVisuals = async () => {
    if (!paragraphs.length) return;

    const validParagraphs = paragraphs.filter(p => p.trim());

    setStoryWithImages(validParagraphs.map((text, index) => ({
      text,
      image: null,
      isLoading: true,
      index
    })));

    let firstGenId = null; // To store the styleRefId

    if (visualMode === 'cartoon') {
      const selectedChild = children.find(child => child.cartoonImage === selectedCartoonStyle);

      // 1. Generate the first image separately
      const firstParagraph = validParagraphs[0];
      const firstImageResult = await generateCartoonImageForParagraph(firstParagraph, 0, selectedChild.cartoonImageId, null); 

      if (firstImageResult && firstImageResult.styleRefId) {
        firstGenId = firstImageResult.styleRefId;
        firstGenerationIdRef.current = firstGenId; 
        setFirstGenerationId(firstGenId); 

        // Update the first image in storyWithImages
        setStoryWithImages(prev => prev.map(item =>
          item.index === 0 ? {
            ...item,
            image: firstImageResult.image,
            isLoading: false
          } : item
        ));

        // 2. Generate the rest of the images using the styleRefId
        validParagraphs.forEach(async (paragraph, index) => {
          if (index > 0) {
            generateCartoonImageForParagraph(paragraph, index, selectedChild.cartoonImageId, firstGenId);
          }
        });
      } else {
        validParagraphs.forEach(async (paragraph, index) => {
          generateCartoonImageForParagraph(paragraph, index, selectedChild.cartoonImageId);
        });
      }
    } else {
      // Generate images for all paragraphs using the default method
      validParagraphs.forEach(async (paragraph, index) => {
        generateImageForParagraph(paragraph, index, randomSeed);
      });
    }
  };

  const generateCartoonImageForParagraph = async (paragraph, index, imageId, firstGenerationId) => {
    const baseUrl = config?.backend_url || 'http://localhost:3002';

    try {
      let styleRefId = null;
      let requestBody = {
        email,
        imageId,
        paragraphs: [paragraph]
      };

      if (index > 0 && firstGenerationId) {
        requestBody.styleRefId = firstGenerationId;
      }

      const response = await axios.post(`${baseUrl}/user/cartoonImage`, requestBody);

      if (response.data.styleRefId) {
        styleRefId = response.data.styleRefId;
      }

      setStoryWithImages(prev => prev.map(item =>
        item.index === index ? {
          ...item,
          image: response.data.images[0],
          isLoading: false
        } : item
      ));
      return { styleRefId: styleRefId, image: response.data.images[0] };
    } catch (error) {
      console.error(`Failed to generate cartoon image ${index}:`, error);
      setStoryWithImages(prev => prev.map(item =>
        item.index === index ? {
          ...item,
          error: true,
          isLoading: false
        } : item
      ));
      return { styleRefId: null, image: null };
    }
  };

  const generateImageForParagraph = async (paragraph, index, randomSeed) => {
    const baseUrl = config?.backend_url || 'http://localhost:3002';
    
    try {
      const response = await axios.post(`${baseUrl}/user/generateImage`, {
        email,
        visualPrompt: visualIdeas,
        paragraphs: [paragraph],
        seed: randomSeed
      });

      // Update individual image slot when ready
      setStoryWithImages(prev => prev.map(item => 
        item.index === index ? {
          ...item,
          image: response.data.images[0],
          isLoading: false
        } : item
      ));

    } catch (error) {
      console.error(`Failed to generate image ${index}:`, error);
      setStoryWithImages(prev => prev.map(item => 
        item.index === index ? {
          ...item,
          error: true,
          isLoading: false
        } : item
      ));
    }
  };

  const handleRegenerateImage = async (index) => {

    const paragraphToRegenerate = storyWithImages[index].text;
    

    setStoryWithImages(prev => prev.map(item => 
      item.index === index ? {
        ...item,
        isLoading: true,
        error: false
      } : item
    ));
    
    try {

      if (visualMode === 'cartoon') {
        const selectedChild = children.find(child => child.cartoonImage === selectedCartoonStyle);
        if(firstGenerationIdRef.current){
          generateCartoonImageForParagraph(paragraphToRegenerate, index, selectedChild.cartoonImageId, firstGenerationIdRef.current);
        }else {
          generateCartoonImageForParagraph(paragraphToRegenerate, index, selectedChild.cartoonImageId);
        }
      } else {
        generateImageForParagraph(paragraphToRegenerate, index, randomSeed);
      }

    } catch (error) {
      console.error(`Failed to regenerate image ${index}:`, error);
      setStoryWithImages(prev => prev.map(item => 
        item.index === index ? {
          ...item,
          error: true,
          isLoading: false
        } : item
      ));
      toast.error("Failed to regenerate image");
    }
  };

  // Update handleSaveStory with navigation
  const handleSaveStory = async () => {
    try {
      const baseUrl = config?.backend_url || 'http://localhost:3002';
      const token = localStorage.getItem("token");

      const validStoryList = storyWithImages
        .filter(item => item.text.trim())
        .map((item, index) => ({
          line: item.text,
          image: item.image,
          id: index
        }));

      const storyData = {
        email,
        title,
        content: validStoryList.map(item => item.line).join('\n\n'),
        storyList: validStoryList,
        prompt: storyIdeas,
        thumbnail: selectedThumbnail || validStoryList[0]?.image,
        status: storyStatus,
        tag: tags
      };
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.post(`${baseUrl}/user/save`, storyData, { headers });
      toast.success('Story saved! Generating a quiz...');
      const quizResponse = await axios.post(`${baseUrl}/generateQuiz`, {
        content: storyData.content
      });
      
      navigate(`/generateQuiz/${response.data.id}`, {
        state: {
          storyId: response.data.id,
          storyContent: storyData.content,
          title: storyData.title,
          generatedQuiz: quizResponse.data.data
        }
      });
      
    } catch (error) {
      toast.error("Failed to save story");
    }
  };

  // Update handleRejectStory with better error handling
  const handleRejectStory = async () => {
    try {
      const baseUrl = config?.backend_url || 'http://localhost:3002';
      const token = localStorage.getItem("idToken");

      // First save the story with rejected status
      const validStoryList = storyWithImages
        .filter(item => item.text.trim())
        .map((item, index) => ({
          line: item.text,
          image: item.image,
          id: index
        }));

      const storyData = {
        email,
        title,
        content: validStoryList.map(item => item.line).join('\n\n'),
        storyList: validStoryList,
        prompt: storyIdeas,
        thumbnail: selectedThumbnail || validStoryList[0]?.image,
        status: 'rejected',
        tag: tags
      };

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Save story first to get an ID
      await axios.post(`${baseUrl}/user/save`, storyData, { headers });
      // const newStoryId = saveResponse.data.id;

      // // Then call reject API with the new story ID
      // await axios.post(`${baseUrl}/user/reject-story`, { storyId: newStoryId });
      
      toast.info("Story discarded");
      
      
      // Reset all form states
    setStoryWithImages(selectedImages);
    setStory("");
    setParagraphs([]);
    setTitle("");
    setTags([]);
    setStoryIdeas(defaultStoryIdea);
    setVisualIdeas(defaultVisualIdea);
    setSelectedThumbnail("");
    setStoryStatus("public");
    setIsEditing(false);
    
    // Show the initial form
    setShowForm(true);
    setActiveTab("story");
    
    // Stay on create story page instead of navigating away
    navigate('/create');
    } catch (error) {
      console.error("Reject Story Error:", error);
      toast.error("Failed to reject story");
    } finally {
      setStoryWithImages([]);
      setStory("");
      setParagraphs([]);
      setTitle("");
      setTags([]);
      setShowForm(true);
      setActiveTab("story");
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setSelectedImagesList(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // const handleGoBack = () => {
  //   if (story || storyWithImages.length > 0) {
  //     const confirmed = window.confirm('Are you sure you want to go back? Any unsaved changes will be lost.');
  //     if (!confirmed) return;
  //   }
  //   navigate('/create');
  // };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Generating your story...</p>
      </div>
    );
  }

  return (
    <>
      <div className="story-form">
        {showForm ? (
          <>
            {selectedImagesList.length > 0 && (
              <div className="image-preview-grid">
                {selectedImagesList.map((image, index) => (
                  <div key={index} className="image-thumbnail-container">
                    <button 
                      className="remove-image-btn"
                      onClick={() => handleRemoveImage(index)}
                    >
                      ✕
                    </button>
                    <img src={image} alt={`Upload ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Ideas for the story:</label>
              <textarea
                className="form-textarea"
                placeholder={defaultStoryIdea}
                value={storyIdeas}
                onChange={(e) => setStoryIdeas(e.target.value)}
                rows="6"
              />
            </div>

            <div className="visual-options">
              <label className="visual-style-label">Choose how to style your story visuals:</label>
              <div className="visual-style-choice">
                <div className="style-option">
                  <input 
                    type="radio" 
                    id="prompt-style"
                    name="visual-style"
                    checked={visualMode === 'prompt'}
                    onChange={() => setVisualMode('prompt')}
                  />
                  <label htmlFor="prompt-style">
                    <span className="style-title">Text Description</span>
                    <span className="style-description">Describe how characters and scenes should look</span>
                  </label>
                </div>
                <div className="style-option">
                  <input 
                    type="radio" 
                    id="cartoon-style"
                    name="visual-style"
                    checked={visualMode === 'cartoon'}
                    onChange={() => setVisualMode('cartoon')}
                  />
                  <label htmlFor="cartoon-style">
                    <span className="style-title">Cartoon Style</span>
                    <span className="style-description">Use existing cartoon style from your child's profile</span>
                  </label>
                </div>
              </div>

              {visualMode === 'prompt' ? (
                <div className="visual-prompt">
                  <textarea 
                    className="form-textarea"
                    placeholder={defaultVisualIdea}
                    value={visualIdeas}
                    onChange={(e) => setVisualIdeas(e.target.value)}
                    rows="6"
                  />
                </div>
              ) : (
                <div className="cartoon-grid">
                  {children.length === 0 || !children.some(child => child.cartoonImage) ? (
                    <div className="cartoon-empty-state">
                      <span className="info-icon">⚠️</span>
                      <p>No cartoon avatars available</p>
                      <Link to="/profile" className="create-avatar-link">
                        Create avatars in profile
                      </Link>
                    </div>
                  ) : (
                    children.map(child => (
                      child.cartoonImage && (
                        <div 
                          key={child._id} 
                          className={`cartoon-option ${selectedCartoonStyle === child.cartoonImage ? 'selected' : ''}`}
                          onClick={() => setSelectedCartoonStyle(child.cartoonImage)}
                        >
                          <img src={child.cartoonImage} alt={`${child.name}'s style`} />
                          <p>{child.name}'s Style</p>
                        </div>
                      )
                    ))
                  )}
                </div>
              )}
            </div>

            <button 
              className="generate-button"
              onClick={handleGenerateStory}
              disabled={loading || !storyIdeas.trim() || 
                (visualMode === 'prompt' ? !visualIdeas.trim() : !selectedCartoonStyle)}
            >
              {loading ? "Generating..." : "Generate Story"}
            </button>
          </>
        ) : (
          <div className="story-content">
            <div className="story-section">
              <div className="title-section">
                {isEditingTitle ? (
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => setIsEditingTitle(false)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        setIsEditingTitle(false);
                      }
                    }}
                    className="title-input"
                    autoFocus
                  />
                ) : (
                  <h2 onClick={() => setIsEditingTitle(true)} className="editable-title">
                    {title || 'Click to add title'}
                    <span className="edit-icon">✎</span>
                  </h2>
                )}
              </div>
              {storyWithImages.length > 0 ? (
                <>
                  <div className="story-with-visuals">
                    {storyWithImages
                      .filter(item => item.text.trim()) // Only show non-empty paragraphs
                      .map((item, index) => (
                        <React.Fragment key={index}>
                          <div className="visual-container">
                            {item.isLoading ? (
                              <div className="image-loader">
                                <div className="spinner"></div>
                                <p>Generating image...</p>
                              </div>
                            ) : item.error ? (
                              <div className="image-error">
                                <p>Failed to generate image</p>
                                <button onClick={() => handleRegenerateImage(index)}>
                                  Try Again
                                </button>
                              </div>
                            ) : (
                              <div className="image-wrapper">
                                <ProgressiveImage 
                                  src={item.image} 
                                  alt={`Story visual ${index + 1}`}
                                  onRegenerate={() => handleRegenerateImage(index)}
                                  index={index}
                                />
                              </div>
                            )}
                          </div>
                          <div className="paragraph-container">
                            <div className="story-text">{item.text}</div>
                          </div>
                        </React.Fragment>
                      ))}
                  </div>
                </>
              ) : (
                <div className="story-box">
                  {isEditing ? (
                    <textarea
                      value={story}
                      onChange={(e) => setStory(e.target.value)}
                      className="story-textarea"
                    />
                  ) : (
                    <div className="story-text">
                      {story}
                    </div>
                  )}
                </div>
              )}
              <div className="tags-container">
                {tags.map((tag, index) => (
                  <span key={index} className="tag">#{tag}</span>
                ))}
              </div>
              <div className="action-buttons">
                {!storyWithImages.length ? (
                  <>
                    <button 
                      onClick={() => {
                        if (isEditing) {
                          handleEditSave();  // Call handleEditSave when saving edits
                        } else {
                          setIsEditing(true);
                        }
                      }} 
                      // disabled={generatingVisuals}
                    >
                      {isEditing ? "Save" : "Edit Story"}
                    </button>
                    <button onClick={handleCreateVisuals} disabled={isEditing}>
                      Create Visuals
                    </button>
                  </>
                ) : (
                  <div className="final-actions">
                    <div className="thumbnail-selector">
                      <h3>Select Thumbnail</h3>
                      <div className="thumbnail-grid">
                        {storyWithImages.map((item, index) => (
                          <img 
                            key={index}
                            src={item.image}
                            onClick={() => setSelectedThumbnail(item.image)}
                            className={selectedThumbnail === item.image ? 'selected' : ''}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="status-selector">
                      <label className="status-label">Choose Story Visibility</label>
                      <select 
                        value={storyStatus} 
                        onChange={(e) => setStoryStatus(e.target.value)}
                      >
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                      </select>
                    </div>
                    <div className="story-actions">
                      <button className="discard-button" onClick={handleRejectStory}>Discard</button>
                      <button className="save-button" onClick={handleSaveStory}>Save Story</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StoryForm;